import React from "react";
import { useSpeechInput } from "../hooks/use-speech-input";

interface SpeechTextareaProps {
  value: string;
  onChange: (value: string) => void;
  accent?: string;
  accentBg?: string;
  border?: string;
  background?: string;
  placeholder?: string;
  maxLength?: number;
  disabled?: boolean;
  autoFocus?: boolean;
  rows?: number;
  minHeight?: number;
  resize?: React.CSSProperties["resize"];
  style?: React.CSSProperties;
}

export function SpeechTextarea({
  value,
  onChange,
  accent = "rgba(126,184,255,0.9)",
  accentBg = "rgba(126,184,255,0.12)",
  border = "rgba(126,184,255,0.3)",
  background = "rgba(126,184,255,0.08)",
  placeholder,
  maxLength,
  disabled,
  autoFocus,
  rows,
  minHeight = 80,
  resize = "vertical",
  style,
}: SpeechTextareaProps) {
  const { isRecording, isSupported, toggle } = useSpeechInput(onChange);

  return (
    <div style={{ position: "relative" }}>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        maxLength={maxLength}
        disabled={disabled}
        autoFocus={autoFocus}
        rows={rows}
        style={{
          width: "100%",
          minHeight,
          resize,
          background,
          border: `1px solid ${border}`,
          borderRadius: 6,
          color: "#e8f0ff",
          padding: "10px 12px",
          paddingBottom: isSupported ? 48 : 12,
          fontSize: 13,
          fontFamily: "inherit",
          lineHeight: 1.5,
          outline: "none",
          ...style,
        }}
      />
      {isSupported && (
        <div
          style={{
            position: "absolute",
            bottom: 8,
            left: 0,
            right: 0,
            display: "flex",
            justifyContent: "center",
          }}
        >
          <button
            type="button"
            onClick={() => toggle(value)}
            disabled={disabled}
            style={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              border: "none",
              background: isRecording ? "rgba(255,80,80,0.25)" : accentBg,
              cursor: disabled ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: isRecording
                ? "0 0 0 3px rgba(255,80,80,0.3), 0 0 14px rgba(255,80,80,0.2)"
                : `0 0 0 3px ${accentBg}, 0 0 12px ${accentBg}`,
              transition: "box-shadow 0.3s ease",
              animation: isRecording ? "micPulse 1.2s ease-in-out infinite" : "none",
            }}
          >
            {isRecording ? (
              <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                <rect x="4" y="4" width="12" height="12" rx="2" fill="rgba(255,80,80,0.9)" />
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                <rect x="7" y="2" width="6" height="11" rx="3" fill={accent} />
                <path d="M4 9.5a6 6 0 0 0 12 0" stroke={accent} strokeWidth="1.5" fill="none" strokeLinecap="round" />
                <line x1="10" y1="15.5" x2="10" y2="18" stroke={accent} strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            )}
          </button>
        </div>
      )}
      <style>{`
        @keyframes micPulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(255,100,100,0.4); }
          50% { box-shadow: 0 0 0 8px rgba(255,100,100,0); }
        }
      `}</style>
    </div>
  );
}
